import { FunctionComponent, useEffect, useState } from 'react';

type Phase = 'idle' | 'run' | 'done';

// Solo transform y opacity. La barra aparece recién a los 120 ms: una navegación
// con el chunk ya en caché resuelve antes y no llega a pintarse. Mientras espera
// se queda en 70%; al confirmarse completa y se desvanece.
const CSS = `
.agora-nav-progress{transform:scaleX(0);opacity:0;transform-origin:left center}
.agora-nav-progress[data-phase="run"]{transform:scaleX(0.7);opacity:1;transition:transform 320ms cubic-bezier(0.22,1,0.36,1) 120ms,opacity 200ms cubic-bezier(0.22,1,0.36,1) 120ms}
.agora-nav-progress[data-phase="done"]{transform:scaleX(1);opacity:0;transition:transform 200ms cubic-bezier(0.4,0,0.2,1),opacity 200ms cubic-bezier(0.4,0,0.2,1) 120ms}
@media (prefers-reduced-motion:reduce){
  .agora-nav-progress,.agora-nav-progress[data-phase]{transform:none}
  .agora-nav-progress[data-phase="run"]{transition:opacity 200ms linear 120ms}
  .agora-nav-progress[data-phase="done"]{transition:opacity 120ms linear}
}
`;

/** Barra de 2px arriba de todo mientras la ruta destino no terminó de cargar. */
export const NavigationProgress: FunctionComponent<{ active: boolean }> = ({ active }) => {
  // Si la barra llegó a pintarse en esta navegación. La fase sale de `active` en cada render:
  // una fase guardada aparte podía quedar en 'run' con la navegación ya confirmada (cambiar de
  // sección en Analítica la dejaba al 70% hasta recargar).
  const [painted, setPainted] = useState(false);

  useEffect(() => {
    if (!active) return;
    // Un frame en 0 para que la transición arranque desde ahí.
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setPainted(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [active]);

  useEffect(() => {
    if (active || !painted) return;
    const timer = window.setTimeout(() => setPainted(false), 320);
    return () => window.clearTimeout(timer);
  }, [active, painted]);

  const phase: Phase = !painted ? 'idle' : active ? 'run' : 'done';

  return (
    <>
      <style>{CSS}</style>
      <div
        aria-hidden
        data-phase={phase === 'idle' ? undefined : phase}
        // Capa de las barras de la app: se monta después del Router, así que en el empate
        // con las barras sticky (mismo z) queda arriba, como con el z-[60] de antes.
        className="agora-nav-progress pointer-events-none fixed inset-x-0 top-0 z-sticky h-[2px] bg-frost-brand"
      />
    </>
  );
};
